import React from 'react';
import { Label } from '@fluentui/react-components';
import {
    ChevronDownRegular,
    ChevronRightRegular,
    ErrorCircleRegular
} from '@fluentui/react-icons';

export const CAF_ERROR_ANALYSIS_FIELDS = [
    'summary',
    'failureSignature',
    'affectedComponent',
    'rootCause',
    'evidence',
    'resolution',
    'prevention',
    'dataGaps'
] as const;

type CafErrorAnalysisField = typeof CAF_ERROR_ANALYSIS_FIELDS[number];

type JsonObject = Record<string, unknown>;

export interface CafAnalysisReference {
    number?: number;
    type: string;
    id: string;
    title: string;
    emoji?: string;
    url?: string;
}

export interface CafErrorAnalysis extends Record<CafErrorAnalysisField, string> {
    references: CafAnalysisReference[];
    relatedSources: CafAnalysisReference[];
}

export interface FailureAnalysisData {
    requestId: string;
    parsed: CafErrorAnalysis | null;
    analysisText: string;
    error?: string;
}

interface AnalysisSection {
    key: CafErrorAnalysisField;
    title: string;
    emptyText: string;
    defaultOpen?: boolean;
}

const ANALYSIS_SECTIONS: AnalysisSection[] = [
    { key: 'summary', title: 'Summary', emptyText: 'No summary available.', defaultOpen: true },
    { key: 'failureSignature', title: 'Failure Signature', emptyText: 'No failure signature available.' },
    { key: 'affectedComponent', title: 'Affected Component', emptyText: 'No affected component identified.' },
    { key: 'rootCause', title: 'Root Cause', emptyText: 'No root cause established.' },
    { key: 'evidence', title: 'Evidence', emptyText: 'No supporting evidence available.' },
    { key: 'resolution', title: 'Resolution', emptyText: 'No resolution available.' },
    { key: 'prevention', title: 'Prevention', emptyText: 'No prevention measures available.' },
    { key: 'dataGaps', title: 'Data Gaps', emptyText: 'No data gaps reported.' }
];

function isJsonObject(value: unknown): value is JsonObject {
    return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function getCaseInsensitiveProperty(source: JsonObject, propertyName: string): unknown {
    const matchingKey = Object.keys(source).find(key => key.toLowerCase() === propertyName.toLowerCase());
    return matchingKey === undefined ? undefined : source[matchingKey];
}

function hasCaseInsensitiveProperty(source: JsonObject, propertyName: string): boolean {
    return Object.keys(source).some(key => key.toLowerCase() === propertyName.toLowerCase());
}

function normalizeText(value: unknown): string {
    if (typeof value === 'string') {
        return value;
    }

    if (value === null || value === undefined) {
        return '';
    }

    return JSON.stringify(value, null, 2) ?? String(value);
}

function normalizeReferences(value: unknown): CafAnalysisReference[] {
    if (!Array.isArray(value)) {
        return [];
    }

    return value
        .filter(isJsonObject)
        .map(reference => ({
            number: typeof reference.number === 'number' ? reference.number : undefined,
            type: normalizeText(reference.type),
            id: normalizeText(reference.id),
            title: normalizeText(reference.title),
            emoji: normalizeText(reference.emoji) || undefined,
            url: normalizeText(reference.url) || undefined
        }));
}

function stripCodeBlockMarkers(content: string): string {
    return content
        .trim()
        .replace(/^```\w*\s*\n?/i, '')
        .replace(/\n?```\s*$/i, '')
        .trim();
}

export function parseFailureAnalysis(analysisText: string): CafErrorAnalysis | null {
    try {
        const parsed: unknown = JSON.parse(stripCodeBlockMarkers(analysisText));
        if (!isJsonObject(parsed) || !CAF_ERROR_ANALYSIS_FIELDS.every(field => hasCaseInsensitiveProperty(parsed, field))) {
            return null;
        }

        return {
            summary: normalizeText(getCaseInsensitiveProperty(parsed, 'summary')),
            failureSignature: normalizeText(getCaseInsensitiveProperty(parsed, 'failureSignature')),
            affectedComponent: normalizeText(getCaseInsensitiveProperty(parsed, 'affectedComponent')),
            rootCause: normalizeText(getCaseInsensitiveProperty(parsed, 'rootCause')),
            evidence: normalizeText(getCaseInsensitiveProperty(parsed, 'evidence')),
            resolution: normalizeText(getCaseInsensitiveProperty(parsed, 'resolution')),
            prevention: normalizeText(getCaseInsensitiveProperty(parsed, 'prevention')),
            dataGaps: normalizeText(getCaseInsensitiveProperty(parsed, 'dataGaps')),
            references: normalizeReferences(getCaseInsensitiveProperty(parsed, 'references')),
            relatedSources: normalizeReferences(getCaseInsensitiveProperty(parsed, 'relatedSources'))
        };
    } catch {
        return null;
    }
}

function CollapsibleSection({ title, children, defaultOpen = false }: { title: string, children: React.ReactNode, defaultOpen?: boolean }) {
    const [open, setOpen] = React.useState(defaultOpen);

    return (
        <div style={{ border: '1px solid #e0e0e0', borderRadius: 4, marginBottom: 10, background: '#fafbfc' }}>
            <div
                style={{ cursor: 'pointer', padding: '10px 16px', fontWeight: 500, display: 'flex', alignItems: 'center', userSelect: 'none', color: '#242424' }}
                onClick={() => setOpen(!open)}
                onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        setOpen(!open);
                    }
                }}
                role="button"
                tabIndex={0}
                aria-expanded={open}
            >
                {open
                    ? <ChevronDownRegular style={{ marginRight: 8, fontSize: 14 }} />
                    : <ChevronRightRegular style={{ marginRight: 8, fontSize: 14 }} />}
                {title}
            </div>
            {open && (
                <div style={{ padding: '12px 16px', borderTop: '1px solid #e0e0e0', background: '#fff', whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: '#242424' }}>
                    {children}
                </div>
            )}
        </div>
    );
}

function getExternalReferenceUrl(reference: CafAnalysisReference): string | undefined {
    if (!reference.url) {
        return undefined;
    }

    try {
        const url = new URL(reference.url);
        return url.protocol === 'https:' || url.protocol === 'http:' ? url.toString() : undefined;
    } catch {
        return undefined;
    }
}

function ReferenceList({ title, references }: { title: string, references: CafAnalysisReference[] }) {
    if (references.length === 0) {
        return null;
    }

    return (
        <CollapsibleSection title={title}>
            <ol style={{ margin: 0, paddingLeft: 24 }}>
                {references.map((reference, index) => {
                    const externalUrl = getExternalReferenceUrl(reference);
                    const referenceTitle = reference.title || reference.id || `Reference ${index + 1}`;
                    const content = <>{reference.emoji ? `${reference.emoji} ` : ''}{referenceTitle}</>;

                    return (
                        <li key={`${reference.type}-${reference.id}-${reference.number ?? index}`} style={{ marginBottom: 6 }}>
                            {externalUrl ? (
                                <a href={externalUrl} target="_blank" rel="noopener noreferrer">
                                    {content}
                                </a>
                            ) : content}
                        </li>
                    );
                })}
            </ol>
        </CollapsibleSection>
    );
}

export function FailureAnalysis({ data }: { data: FailureAnalysisData }) {
    if (data.error) {
        return (
            <div className="failure-analysis-error">
                <ErrorCircleRegular style={{ color: '#A80000', marginRight: '8px' }} />
                <span>{data.error}</span>
            </div>
        );
    }

    if (!data.parsed) {
        return (
            <div className="failure-analysis-details">
                <Label style={{ marginTop: '10px', fontWeight: 'bold', display: 'block' }}>
                    CAF Error Analysis for Request ID: {data.requestId}
                </Label>
                <div className="analysis-content llm-content">
                    <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                        {data.analysisText || 'No analysis available'}
                    </pre>
                </div>
            </div>
        );
    }

    return (
        <div className="failure-analysis-details">
            <Label style={{ marginTop: '10px', fontWeight: 'bold', display: 'block' }}>
                CAF Error Analysis for Request ID: {data.requestId}
            </Label>
            <div className="analysis-content llm-content" style={{ marginTop: 8 }}>
                {ANALYSIS_SECTIONS.map(section => (
                    <CollapsibleSection key={section.key} title={section.title} defaultOpen={section.defaultOpen}>
                        {data.parsed?.[section.key] || section.emptyText}
                    </CollapsibleSection>
                ))}
                <ReferenceList title="References" references={data.parsed.references} />
                <ReferenceList title="Related Sources" references={data.parsed.relatedSources} />
            </div>
        </div>
    );
}
